import React, { useCallback, useEffect, useState } from 'react'
import { ThumbUpIcon } from '@heroicons/react/solid'
import { useAutoComplete } from '@/hooks/useAutoComplete'
import {
    checkFeedbackGivenLast60days,
    postNpsFeedback,
} from '@/services/SearchService'
import { useApplicationShellStore } from '@/store/store'
import { uuidv4 } from '@/utilities/contents'

type NpsFeedbackPayload = {
    rating: string
    comment: string
}

const UserRatingPopup: React.FC = () => {
    const ratings = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    const [rating, setRating] = useState(0)
    const [comment, setComment] = useState<string>('')
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
    const [isLoading, setIsloading] = useState<boolean>(false)

    const { getToken } = useAutoComplete()
    const addMessage = useApplicationShellStore(
        useCallback(state => state.addMessage, []),
    )
    const checkFeedbackSatus = async () => {
        // if(window.sessionStorage.getItem("feedback_checked")){
        //     setIsFormOpen(false);
        // }else{
        const token = await getToken()
        const feedbackGiven = await checkFeedbackGivenLast60days(token)
        if (!feedbackGiven) {
            setIsFormOpen(true)
        } else {
            setIsFormOpen(false)
        }
        //     window.sessionStorage.setItem("feedback_checked","ture");
        // }
    }
    React.useEffect(() => {
        checkFeedbackSatus()
    }, [])

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(event.target.value)
    }
    const handleSubmit = async () => {
        if (rating > 0 && !isLoading) {
            setIsloading(true)
            const payload = { rating: rating.toString(), comment: comment }
            const response = await postRating(payload)
            setIsloading(false)
            if (response.message) {
                addMessage({
                    id: uuidv4(),
                    messageType: 'success',
                    message: 'Feedback recorded successfully',
                })
                setIsFormOpen(false)
                clearFormValues()
            } else {
                addMessage({
                    id: uuidv4(),
                    messageType: 'error',
                    message: '!Error while saving',
                })
            }
        }
    }

    const postRating = async (payload: NpsFeedbackPayload) => {
        const token = await getToken()
        return await postNpsFeedback(token, payload)
    }

    const handleCancel = async () => {
        setIsFormOpen(false)
        await postRating({ rating: '0', comment: '' })
        clearFormValues()
    }

    const clearFormValues = () => {
        setRating(0)
        setComment('')
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsFormOpen(false)
        }, 60 * 1000)
        return () => {
            clearTimeout(timer)
        }
    }, [isFormOpen])

    return isFormOpen ? (
        <div className="relative">
            <div
                className="fixed bottom-0 right-3"
                aria-labelledby="modal-title"
                role="dialog"
                aria-modal="true"
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-4 text-center">
                    <div className="inline-block align-bottom bg-orange-100 rounded-lg border-2 border-gray-300 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <ThumbUpIcon className="w-5 h-5 text-orange-600" />
                                </div>
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3
                                        className="text-lg leading-6 font-medium text-gray-900"
                                        id="modal-title"
                                    >
                                        Your feedback is precious to us
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            From 1 to 10, how much would you
                                            recommend using Konnect tools for
                                            your colleagues?
                                        </p>
                                        <ul className="flex justify-center my-4">
                                            {ratings.map(i =>
                                                i <= rating ? (
                                                    <li
                                                        key={i}
                                                        className="cursor-pointer"
                                                        onClick={() =>
                                                            rating === i
                                                                ? setRating(
                                                                      i - 1,
                                                                  )
                                                                : setRating(i)
                                                        }
                                                    >
                                                        <div className="mx-0.5 h-9 w-9 flex justify-center items-center rounded-full p-2 text-white bg-primary border border-color:gray-500">
                                                            {i}
                                                        </div>
                                                    </li>
                                                ) : (
                                                    <li
                                                        key={i}
                                                        className="cursor-pointer"
                                                        onClick={() =>
                                                            rating === i
                                                                ? setRating(
                                                                      i - 1,
                                                                  )
                                                                : setRating(i)
                                                        }
                                                    >
                                                        <div className="mx-0.5 h-9 w-9 flex justify-center items-center rounded-full p-2 bg-gray-200 border border-color:gray-500">
                                                            {i}
                                                        </div>
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        Any comment, you would like to make.
                                        (max 500 characters)
                                    </p>
                                    <textarea
                                        onChange={handleChange}
                                        value={comment}
                                        className="form-textarea caret-gray-800 mt-2 h-20 px-3 py-2 block w-full border rounded-lg focus:shadow-outline"
                                        maxLength={500}
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                disabled={rating === 0}
                                type="button"
                                onClick={handleSubmit}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white focus:outline-none focus:ring-2 hover:bg-secondary-550 focus:ring-offset-2 focus:ring-secondary-550 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-60 disabled:cursor-auto "
                            >
                                Submit
                            </button>
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-550 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ) : null
}

export default UserRatingPopup
